import type { AxiosError } from 'axios'
import api from './api/client'
import type { JsonRequest } from './api/client'
export type { JsonRequest } from './api/client'

// ============================================================================
// Types
// ============================================================================

export type UserRole = 'sales_rep' | 'operations' | 'management' | 'support' | 'finance' | 'super_admin'

export interface User {
  id: string
  name: string
  email: string
  role: UserRole
  is_active: boolean
  two_factor_confirmed_at: string | null
  invited_at?: string | null
  invite_expires_at?: string | null
  invite_accepted_at?: string | null
  password_set_at?: string | null
  last_login_at: string | null
  created_at: string
  updated_at: string
}

export interface Lead {
  id: string
  dba_name: string | null
  legal_name: string | null
  contact_name: string | null
  contact_phone: string | null
  business_email: string | null
  website: string | null
  business_description: string | null
  ownership_type: string | null
  years_in_business: number | null
  monthly_volume: number | null
  avg_ticket: number | null
  high_ticket: number | null
  pct_card_swiped: number | null
  pct_ecommerce: number | null
  pct_cnp: number | null
  stage: string
  sub_stage: string | null
  lead_source: string | null
  assigned_to: string | null
  assigned_to_secondary: string | null
  sales_rep_id: string | null
  has_tax_id: boolean
  has_checking_account: boolean
  notes: string | null
  created_at: string
  updated_at: string
  converted_at: string | null
  merchant_id: string | null
  selected_statement_analysis_id?: string | null
  // Computed by backend via withExists — true when application has
  // validifi_passed=false and validifi_checked_at is set
  validifi_flagged?: boolean
  assigned_user?: User
  assigned_user_secondary?: User
  application?: Application
  pipeline_events?: PipelineEvent[]
}

export interface NorthBoardingSubmissionSummary {
  id: string
  request_id: string
  ext_ref_id: string | null
  linked_submission_id: string | null
  operation: string
  status: string
  http_status: number | null
  as_ref_id: string | null
  decline_reasons: string[]
  error_message: string | null
  started_at: string | null
  sent_at: string | null
  completed_at: string | null
  fast_poll_until: string | null
}

export interface NorthBoardingDryRunResult {
  valid: boolean
  schema_errors: string[]
  missing_configuration: string[]
  warnings: string[]
  send_enabled: boolean
}

export interface Application {
  id: string
  lead_id: string
  merchant_id: string | null
  // Step 1: Merchant Info
  sales_rep_id: string | null
  mpa_type: string
  /** Fiserv boarding platform. Omaha is the column default. */
  platform: 'omaha' | 'north' | null
  /**
   * North bank-level selector. Null until an operator chooses it.
   * Same Day is never a default.
   */
  north_funding_type: 'same_day' | 'next_day' | null
  dba_name: string | null
  legal_name: string | null
  dba_address: string | null
  dba_city: string | null
  dba_state: string | null
  dba_zip: string | null
  legal_address: string | null
  legal_city: string | null
  legal_state: string | null
  legal_zip: string | null
  dba_phone: string | null
  legal_phone: string | null
  website: string | null
  ownership_type: string | null
  paper_statements: boolean
  business_email: string | null
  // Step 2: Business Profile
  business_description: string | null
  business_start_date: string | null
  years_in_business: number | null
  years_owned: number | null
  monthly_volume: number | null
  avg_ticket: number | null
  high_ticket: number | null
  pct_card_swiped: number | null
  pct_ecommerce: number | null
  pct_cnp: number | null
  cardholder_billing_recurring: boolean | null
  transaction_delivery_timing: 'in_0_7' | 'in_8_14' | 'in_15_30' | 'over_30' | null
  // AD-013 Slice 3-Lite (April 28, 2026) — pricing discriminators
  // populated by Step 3 / Step 5 wizard controls; consumed by
  // Slice 4b.1's buildPricing() at SubmitMPA time.
  discount_method_code: string | null
  discount_calc_flag_code: string | null
  statement_interchange_print_code: string | null
  bi_annual_fee_enabled: boolean
  mcc_code: string | null
  // Step 3: Banking (encrypted fields not in response)
  bank_name: string | null
  has_routing_number: boolean
  has_account_number: boolean
  special_instructions: string | null
  // Step 4: Pricing
  pricing_option: 'A' | 'B' | null
  pricing_template_id: string | null
  source_statement_analysis_id?: string | null
  statement_analysis_notice?: string | null
  bankcard_discount_rate: number | null
  bankcard_per_item: number | null
  debit_discount_rate: number | null
  debit_per_item: number | null
  amex_rate: number | null
  amex_per_item: number | null
  discover_rate: number | null
  discover_per_item: number | null
  auth_fee: number | null
  flat_rate_bankcard: number | null
  flat_rate_debit: number | null
  flat_rate_amex: number | null
  flat_rate_discover: number | null
  compliance_program: string | null
  compliance_fee_pct: number | null
  compliance_fee_amt: number | null
  batch_fee: number | null
  maintenance_fee: number | null
  pin_debit_item_rate: number | null
  pin_debit_volume_rate: number | null
  ebt_fns_id: string | null
  ewic_vendor_id: string | null
  // Step 6: Beneficial Ownership (encrypted fields not in response)
  owner_first_name: string | null
  owner_mi: string | null
  owner_last_name: string | null
  owner_title: string | null
  ownership_pct: number | null
  owner_address: string | null
  owner_city: string | null
  owner_state: string | null
  owner_zip: string | null
  owner_phone: string | null
  owner_email: string | null
  owner_id_issuer: string | null
  owner_id_type: string | null
  has_owner_dob: boolean
  has_owner_ssn_last4: boolean
  has_owner_id_number: boolean
  // Control Person (encrypted fields not in response)
  cp_first_name: string | null
  cp_mi: string | null
  cp_last_name: string | null
  cp_title: string | null
  cp_ownership_pct: number | null
  cp_address: string | null
  cp_city: string | null
  cp_state: string | null
  cp_zip: string | null
  cp_phone: string | null
  cp_email: string | null
  cp_id_issuer: string | null
  cp_id_type: string | null
  has_cp_dob: boolean
  has_cp_ssn_last4: boolean
  has_cp_id_number: boolean
  // (Guarantor fields removed — see CLAUDE.md Section 10 corrected
  //  single-signer design. The Beneficial Owner is the sole signer
  //  of the MPA and signs three fields on the same document.)
  // Step 7: Equipment & Services
  terminal_make: string | null
  terminal_model: string | null
  terminal_qty: number | null
  terminal_status: string | null
  pos_make: string | null
  pos_model: string | null
  mobile_make: string | null
  mobile_model: string | null
  pinpad_make: string | null
  pinpad_model: string | null
  saas_fee: number | null
  hardware_program: string | null
  monthly_hardware_fee: number | null
  hardware_term_months: number | null
  hardware_upfront_cost: number | null
  nmi_gateway_enabled: boolean
  authorize_net_enabled: boolean
  agms_pay_enabled: boolean
  clover_security_indicator: '0' | '04' | '21' | '8' | null
  // Wizard state
  current_step: number
  completed_steps: number
  is_locked: boolean
  is_submitted: boolean
  submitted_at: string | null
  // Fiserv tracking
  fiserv_ext_ref_id: string | null
  fiserv_mid: string | null
  fiserv_as_ref_id: string | null
  fiserv_status: string | null
  fiserv_mpa_status: string | null
  fiserv_submitted_at: string | null
  fiserv_last_polled_at: string | null
  fiserv_status_history: Array<{ code?: string | null; information?: string | null; timestamp?: string | null }> | null
  fiserv_credit_officer_comments: string | null
  fiserv_status_errors: Array<{ name?: string | null; message?: string | null }> | null
  fiserv_submission_error: string | null
  fiserv_submission_failed_at: string | null
  /**
   * North boarding summary. Null on an Omaha application.
   * Live send stays off while send_enabled is false.
   */
  north_boarding?: {
    send_enabled: boolean
    latest: NorthBoardingSubmissionSummary | null
  } | null
  // BoldSign tracking
  boldsign_document_id: string | null
  boldsign_sent_at: string | null
  boldsign_completed_at: string | null
  boldsign_signed_pdf_s3_key: string | null
  boldsign_job_status: 'pending' | 'sent' | 'failed' | null
  boldsign_job_error: string | null
  // ValidiFI
  validifi_checked_at: string | null
  validifi_avc_score: number | null
  validifi_account_status: string | null
  validifi_ownership_match: boolean | null
  validifi_nsf_flag: boolean | null
  validifi_passed: boolean | null
  /**
   * Verbatim ValidiFI response plus a `flags` list of triggered
   * flag-condition strings (avc_below_threshold, account_not_open,
   * not_ach_capable, ownership_mismatch, nsf_history). Empty `flags`
   * = clean validation. Used by the Step 4 status card to render
   * per-flag UI; raw response fields are vendor-specific and only
   * useful for audit/debugging.
   */
  validifi_raw_response: { flags?: string[] } & Record<string, unknown> | null
  // Meta
  created_at: string
  updated_at: string
  lead?: Lead
  pricing_template?: PricingTemplate
}

export interface BoardingReadinessItem {
  code: string
  label: string
  location: string
  action: string
}

export interface BoardingReadinessGroup {
  key: string
  label: string
  severity: 'error' | 'warning' | 'info'
  items: BoardingReadinessItem[]
}

export interface BoardingReadiness {
  status: 'ready' | 'needs_attention' | 'blocked'
  summary: {
    total: number
    error: number
    warning: number
    info: number
  }
  groups: BoardingReadinessGroup[]
}

export interface PipelineEvent {
  id: string
  lead_id: string
  from_stage: string | null
  to_stage: string
  triggered_by: 'manual' | 'boldsign_webhook' | 'fiserv_poll' | 'fiserv_webhook' | 'ai_intake' | 'system'
  user_id: string | null
  note: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  user?: User
}

export interface PricingTemplate {
  id: string
  name: string
  pricing_option: 'A' | 'B'
  // Rate fields — mirror applications table pricing fields, all nullable
  bankcard_discount_rate: number | null
  bankcard_per_item: number | null
  debit_discount_rate: number | null
  debit_per_item: number | null
  amex_rate: number | null
  amex_per_item: number | null
  discover_rate: number | null
  discover_per_item: number | null
  auth_fee: number | null
  flat_rate_bankcard: number | null
  flat_rate_debit: number | null
  flat_rate_amex: number | null
  flat_rate_discover: number | null
  pin_debit_item_rate: number | null
  pin_debit_volume_rate: number | null
  compliance_program: string | null
  compliance_fee_pct: number | null
  compliance_fee_amt: number | null
  batch_fee: number | null
  maintenance_fee: number | null
  is_default: boolean
  is_active: boolean
  created_at: string
}

export interface PaginatedResponse<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
}

export type AccountAnchor = { kind: 'lead'; id: string } | { kind: 'merchant'; id: string }

export interface AccountNote {
  id: string
  body: string
  author: Pick<User, 'id' | 'name'> | null
  created_at: string | null
  lead_id: string | null
  merchant_id: string | null
  can_delete: boolean
}

export interface NotePayload {
  body: string
}

export type AppointmentType = 'call' | 'visit' | 'video'
export type AppointmentStatus = 'scheduled' | 'completed' | 'no_show' | 'canceled'

export interface Appointment {
  id: string
  appointable_type: 'lead' | 'merchant'
  appointable_id: string
  account_name: string | null
  assigned_user: Pick<User, 'id' | 'name'> | null
  starts_at: string
  duration_minutes: number
  type: AppointmentType
  location: string | null
  notes: string | null
  status: AppointmentStatus
  external_calendar_event_id: string | null
  reminder_offset_minutes: number | null
  reminder_due_at: string | null
  reminder_sent_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface AppointmentPayload {
  starts_at: string
  duration_minutes: number
  type: AppointmentType
  location?: string | null
  notes?: string | null
  assigned_user_id?: string
  reminder_offset_minutes?: number | null
}

export interface AppointmentUpdatePayload {
  type?: AppointmentType
  duration_minutes?: number
  location?: string | null
  notes?: string | null
  assigned_user_id?: string
  reminder_offset_minutes?: number | null
}

export interface AppointmentSettings {
  reminder_offset_minutes: number | null
}

export interface GoogleCalendarConnectionStatus {
  connected: boolean
  status: 'connected' | 'disconnected' | 'reconnect_required'
  google_email: string | null
  reconnect_required: boolean
  last_synced_at: string | null
  last_error: string | null
}

export interface AccountActivity {
  id: string
  event_type: string
  summary: string
  metadata: {
    type?: string
    status?: string
    starts_at?: string
    duration_minutes?: number
    location?: string | null
    notes?: string | null
  } | null
  appointment_id: string | null
  user: Pick<User, 'id' | 'name'> | null
  created_at: string | null
}

export interface AppointmentListQuery {
  from?: string
  to?: string
  assigned_user_id?: string
}

export interface ValidationError {
  message: string
  errors: Record<string, string[]>
}

export type ApiError = AxiosError<ValidationError>

// ============================================================================
// Auth API
// ============================================================================

export type LoginRequest = JsonRequest<'auth.login'>

export interface LoginResponse {
  status?: 'needs_2fa' | 'needs_2fa_enrollment'
  user?: User
  challenge_token?: string
  message: string
}

export interface TwoFactorSetupResponse {
  provisioning_uri: string
  qr_svg: string
  manual_secret: string
  recovery_codes: string[]
}

// ============================================================================
// User Management API
// ============================================================================

export interface UserFilters {
  search?: string
  role?: UserRole | ''
  page?: number
  per_page?: number
}

export interface CreateUserRequest {
  name: string
  email: string
  role: UserRole
}

export interface UpdateUserRequest {
  name?: string
  role?: UserRole
  is_active?: boolean
}

// ============================================================================
// Pricing Template Admin API
// ============================================================================

export type PricingOption = 'A' | 'B'
export type ComplianceProgram = 'none' | 'cash_discount' | 'dual_pricing' | null
type RateInput = string | number | null

export interface PricingTemplatePayload {
  name: string
  pricing_option: PricingOption
  bankcard_discount_rate: RateInput
  bankcard_per_item: RateInput
  debit_discount_rate: RateInput
  debit_per_item: RateInput
  amex_rate: RateInput
  amex_per_item: RateInput
  discover_rate: RateInput
  discover_per_item: RateInput
  auth_fee: RateInput
  flat_rate_bankcard: RateInput
  flat_rate_debit: RateInput
  flat_rate_amex: RateInput
  flat_rate_discover: RateInput
  compliance_program: ComplianceProgram
  compliance_fee_pct: RateInput
  compliance_fee_amt: RateInput
  batch_fee: RateInput
  maintenance_fee: RateInput
  pin_debit_item_rate: RateInput
  pin_debit_volume_rate: RateInput
}

// ============================================================================
// Lead API
// ============================================================================

export interface LeadFilters {
  stage?: string
  lead_source?: string
  assigned_to?: string
  validifi_flagged?: boolean
  search?: string
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export interface CreateLeadRequest {
  dba_name?: string
  legal_name?: string
  contact_name?: string
  contact_phone?: string
  business_email?: string
  website?: string
  business_description?: string
  ownership_type?: string
  years_in_business?: number
  monthly_volume?: number
  avg_ticket?: number
  high_ticket?: number
  pct_card_swiped?: number
  pct_ecommerce?: number
  pct_cnp?: number
  lead_source?: string
  assigned_to?: string | null
  assigned_to_secondary?: string | null
  sales_rep_id?: string
  has_tax_id?: boolean
  has_checking_account?: boolean
  notes?: string
}

export interface TransitionStageRequest {
  stage: string
  note?: string
}

// ============================================================================
// Application API
// ============================================================================

// ============================================================================
// Deployment Center API
// ============================================================================

export interface DeploymentTask {
  id: string
  merchant_id: string
  application_id: string
  adapter_type: string
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'cancelled' | 'manual'
  attempts: number
  last_error: string | null
  queued_at: string | null
  started_at: string | null
  completed_at: string | null
  vendor_ref_id: string | null
  result_data?: Record<string, unknown> | null
  authorize_net_status?: string | null
  authorize_net_status_label?: string | null
  authorize_net_merchant_id?: string | null
  authorize_net_activation_link?: string | null
  merchant?: { id: string; dba_name: string; status: string }
  application?: { id: string; lead_id: string; dba_name: string }
}

export type TicketPriority = 'normal' | 'high' | 'urgent'
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type TicketCategory = 'billing' | 'technical' | 'onboarding' | 'general'
export type TicketSourceChannel = 'phone' | 'email' | 'internal'
export type TicketSlaTarget = '1 day' | '3 day' | '7 day' | 'beginning of next month' | 'custom'
export type TicketSlaType = '1_day' | '2_3_days' | '7_days' | 'beginning_of_next_month' | 'custom'

export interface TicketTypeChecklistItem {
  id: string
  ticket_type_id: string
  position: number
  text: string
  comment_required_to_complete: boolean
  file_required_to_complete: boolean
  created_at?: string
  updated_at?: string
}

export interface TicketType {
  id: string
  name: string
  type_group: string | null
  default_priority: TicketPriority
  default_category: TicketCategory
  default_sla_type: TicketSlaType
  default_sla_custom_days: number | null
  default_subject: string | null
  default_description: string | null
  additional_information: string | null
  allowed_roles: Array<UserRole | 'all'>
  active: boolean
  sort_order: number
  archived_at: string | null
  created_at: string
  updated_at: string
  usage_count?: number
  checklist_items?: TicketTypeChecklistItem[]
}

export interface TicketTypePayload {
  name: string
  type_group?: string | null
  default_priority: TicketPriority
  default_category: TicketCategory
  default_sla_type: TicketSlaType
  default_sla_custom_days?: number | null
  default_subject?: string | null
  default_description?: string | null
  additional_information?: string | null
  allowed_roles: Array<UserRole | 'all'>
  active?: boolean
  sort_order?: number
  checklist_items: Array<{
    position: number
    text: string
    comment_required_to_complete?: boolean
    file_required_to_complete?: boolean
  }>
}

export interface TicketChecklistItem {
  id: string
  ticket_id: string
  position: number
  text: string
  comment_required: boolean
  file_required: boolean
  completed_at: string | null
  completed_by: { id: string; name: string; email: string; role: UserRole } | null
  completion_comment: string | null
  completion_file_name: string | null
}

export interface TicketEventActor {
  id: string
  name: string
  role: string
}

export interface TicketEvent {
  id: string
  ticket_id: string
  event_type:
    | 'created'
    | 'status_changed'
    | 'priority_changed'
    | 'assignee_added'
    | 'assignee_removed'
    | 'comment_added'
    | 'checklist_completed'
    | 'sla_due_changed'
    | 'reopened'
    | 'article_linked'
    | 'article_unlinked'
    | 'article_solved_changed'
  user_id: string | null
  actor: TicketEventActor | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  article?: { id: string; title: string; slug: string } | null
  created_at: string
}

export interface TicketArticleLink {
  id: string
  article_id: string
  title: string
  slug: string
  solved: boolean
  linked_by: { id: string; name: string; role: UserRole } | null
  linked_at: string
}

export interface Ticket {
  id: string
  ticket_number: string
  merchant_id: string | null
  lead_id: string | null
  ticket_type_id?: string | null
  source?: string
  subject: string
  body: string | null
  priority: TicketPriority
  sla_target: TicketSlaTarget | null
  sla_type: TicketSlaType | null
  sla_custom_days: number | null
  sla_due_at: string | null
  alerted_thresholds?: string[]
  status: TicketStatus
  category: TicketCategory
  assigned_to: string | null
  created_by: string | null
  resolved_at: string | null
  first_response_at?: string | null
  resolution_notes: string | null
  source_channel: TicketSourceChannel | 'inbox' | null
  created_at: string
  updated_at: string
  events?: TicketEvent[]
  linked_articles?: TicketArticleLink[]
  merchant?: { id: string; dba_name: string; fiserv_mid: string | null; status: string } | null
  lead?: { id: string; dba_name: string | null; legal_name: string | null; business_email?: string | null; stage: string } | null
  assigned_user?: { id: string; name: string; email: string; role: UserRole } | null
  assignees?: { id: string; name: string; email: string; role: UserRole }[]
  creator?: { id: string; name: string; email: string; role: UserRole } | null
  merchant_email?: string | null
  ticket_type?: Pick<TicketType, 'id' | 'name' | 'type_group' | 'additional_information'> | null
  checklist_items?: TicketChecklistItem[]
  help_article_draft_configured?: boolean
}

export interface TicketCommentAttachment {
  id: string
  file_name: string
  mime_type: string
  file_size: number
  created_at: string
  download_url: string | null
}

export interface TicketComment {
  id: string
  ticket_id: string
  user_id: string | null
  body: string
  is_internal: boolean
  source: 'user' | 'system' | 'merchant_email'
  email_sent_at: string | null
  created_at: string
  updated_at: string
  user?: { id: string; name: string; email: string; role: UserRole } | null
  attachments: TicketCommentAttachment[]
}

export type TicketQueueFilter = 'past_sla' | 'due_24h' | 'unassigned_open' | 'stale' | 'reopened'

export interface TicketFilters {
  status?: TicketStatus[]
  priority?: TicketPriority
  assigned_to?: string
  category?: TicketCategory
  search?: string
  per_page?: number
  queue?: TicketQueueFilter
  ticket_type_id?: string
  subject?: string
  created_from?: string
  created_to?: string
}

export interface HelpGapCategory {
  id: string
  name: string
  slug: string
}

export interface HelpGapTypeRow {
  ticket_type_id: string | null
  name: string
  ticket_count: number
  median_resolve_seconds: number | null
  resolve_samples: number
  article_category: HelpGapCategory | null
  tickets_url: string
  write_article_url: string
}

export interface HelpGapSubjectRow {
  subject_key: string
  name: string
  ticket_count: number
  median_resolve_seconds: number | null
  resolve_samples: number
  article_category: HelpGapCategory | null
  tickets_url: string
  write_article_url: string
}

export interface HelpKnowledgeGapsResponse {
  generated_at: string
  scope: 'team'
  business_timezone: string
  subject_minimum: number
  limit: number
  filters: { from: string; to: string }
  ticket_types: HelpGapTypeRow[]
  subjects: HelpGapSubjectRow[]
  notes: Record<string, string>
}

export interface HelpKnowledgeGapsFilters {
  from?: string
  to?: string
}

export interface HelpArticleHealthArticle {
  id: string
  title: string
  slug: string
  status: ArticleStatus
  category_name: string | null
  views: number
  tickets_solved: number
  suggestion_opens: number
  last_reviewed_at: string | null
  stale: boolean
}

export interface HelpArticleHealthCategory {
  id: string
  name: string
  article_count: number
  published_count: number
  stale_count: number
  views: number
  tickets_solved: number
  suggestion_opens: number
}

export interface HelpKnownIssue {
  id: string
  article_id: string
  title: string
  slug: string
  starts_at: string
  ends_at: string | null
  active: boolean
}

export interface HelpArticleHealthResponse {
  generated_at: string
  scope: 'team'
  stale_after_days: number
  can_edit_stale_after_days: boolean
  minutes_per_avoided_ticket: number
  catalog: {
    articles: number
    stale_articles: number
    views: number
    tickets_solved: number
    suggestion_opens: number
  }
  totals: {
    tickets_avoided: number
    estimated_minutes_saved: number
    estimated_hours_saved: string
    opened: number
    opened_with_ticket: number
    opened_without_article: number
    ticket_without_open: number
    suggestion_rows: number
  }
  articles: HelpArticleHealthArticle[]
  categories: HelpArticleHealthCategory[]
  authors: {
    distinct_authors: number
    article_count: number
  }
  reviewers: {
    distinct_reviewers: number
    articles_with_reviewer: number
    articles_without_reviewer: number
    never_reviewed: number
  }
  formula: {
    tickets_avoided: string
    hours: string
    recording: string
    views: string
    tickets_solved: string
    suggestion_opens: string
    last_reviewed: string
    stale: string
    coverage: string
    personal: string
  }
}

export interface TicketStatsPerson {
  user: { id: string; name: string; role: UserRole }
  is_viewer: boolean
  open: number
  overdue: number
  resolved_this_week: number
  median_resolve_seconds: number | null
  median_first_response_seconds: number | null
  sla_hit_rate: number | null
  sla_hits: number
  sla_eligible: number
  resolve_samples: number
  first_response_samples: number
  links: { open: string; overdue: string }
}

export interface TicketStatsWeekPoint {
  week_start: string
  hits: number
  eligible: number
  rate: number | null
}

export interface TicketStatsResponse {
  generated_at: string
  cache_seconds: number
  scope: 'team' | 'self'
  business_timezone: string
  week_starts_at: string
  stale_after_days: number
  can_edit_stale_after_days: boolean
  reopen_counts: 'since_tracking_began'
  attention: {
    past_sla: number
    due_within_24_hours: number
    unassigned_open: number
    stale: number
    reopened: number
  }
  links: {
    past_sla: string
    due_within_24_hours: string
    unassigned_open: string
    stale: string
    reopened: string
  }
  people: TicketStatsPerson[]
  filters: {
    from: string
    to: string
    priority: TicketPriority | null
    ticket_type_id: string | null
    source: TicketSourceChannel | 'inbox' | null
    applies_to: string[]
  }
  opened_vs_resolved: Array<{ week_start: string; opened: number; resolved: number }>
  sla_over_time: {
    by_week: TicketStatsWeekPoint[]
    by_priority: Array<{ priority: TicketPriority; by_week: TicketStatsWeekPoint[] }>
    by_type: Array<{ ticket_type_id: string | null; name: string; by_week: TicketStatsWeekPoint[] }>
  }
  top_types: Array<{ ticket_type_id: string | null; name: string; count: number }>
  top_merchants: Array<{ merchant_id: string; dba_name: string; count: number }>
  notes: Record<string, string>
}

export interface TicketStatsFilters {
  from?: string
  to?: string
  priority?: TicketPriority
  ticket_type_id?: string
  source?: TicketSourceChannel | 'inbox'
}

export interface CreateTicketPayload {
  merchant_id?: string
  lead_id?: string
  ticket_type_id?: string | null
  subject: string
  body?: string | null
  priority?: TicketPriority
  category: TicketCategory
  assigned_to?: string | string[] | null
  unassigned?: boolean
  source_channel: TicketSourceChannel
  sla_target?: TicketSlaTarget
  sla_type?: TicketSlaType
  sla_custom_days?: number | null
}

// ============================================================================
// Merchant Maintenance API
// ============================================================================

export interface MerchantMaintenanceProfileResponse {
  success: boolean
  errorMessage: string | null
  profile: Record<string, unknown>
}

export interface MerchantMaintenanceUpdateResponse {
  success: boolean
  requestId: string | null
  errorMessage: string | null
  resultData: Record<string, unknown>
}

export interface MerchantMaintenanceStatusResponse {
  status: string
  rawResponse: Record<string, unknown>
}

export interface MerchantMaintenanceEquipmentResponse {
  success: boolean
  equipment: Record<string, unknown>[]
  errorMessage: string | null
}

export type MerchantMaintenanceProfileField =
  | 'business_email'
  | 'business_phone'
  | 'contact_name'
  | 'mailing_address'
  | 'mailing_city'
  | 'mailing_state'
  | 'mailing_zip'

export type MerchantMaintenanceProfilePayload = Partial<Record<MerchantMaintenanceProfileField, string>>

export interface MerchantMaintenanceFeaturePayload {
  tipPrompt?: 'Y' | 'N'
  cashBack?: 'Y' | 'N'
  signatureRequired?: 'Y' | 'N'
  receiptPrintMode?: 'merchant' | 'customer' | 'both'
}

export interface MerchantMaintenanceProgrammingPayload {
  downloadPhone?: string
  terminalIdNumber?: string
}

// ============================================================================
// Merchant Reporting API
// ============================================================================

export interface MerchantReportingResponse<T> {
  success: boolean
  data: T[]
  paging: Record<string, unknown>
  errorMessage: string | null
}

export interface MerchantBatchSummaryRow {
  report_date: string
  terminal_number: string
  batch_number: string
  prepaid_discount: number | null
  percent_keyed: number | null
  average_transaction: number | null
  transaction_count: number | null
  sales: number | null
  returns: number | null
  net: number | null
  raw: Record<string, string>
}

export interface MerchantBatchDetailRow {
  transaction_id: string
  transaction_date: string
  transaction_time: string
  transaction_code: string
  keyed: string
  entry_mode: string
  card_type: string
  card_number: string
  authorization_number: string
  transaction_amount: number | null
  raw: Record<string, string>
}

export interface MerchantPaymentSummaryRow {
  report_date: string
  payment_date: string
  merchant_number: string
  routing_number: string
  account_number_last4: string
  sales: number | null
  returns: number | null
  fees: number | null
  adjustments: number | null
  net: number | null
  raw: Record<string, string>
}

export interface MerchantPaymentDetailRow {
  report_date: string
  payment_date: string
  batch_number: string
  terminal_number: string
  description: string
  amount: number | null
  raw: Record<string, string>
}

export interface MerchantStatementRow {
  merchant_number: string
  statement_date: string
  statement_id: string
}

export interface MerchantStatementsResponse {
  data: MerchantStatementRow[]
}

// ============================================================================
// Pricing Template API
// ============================================================================

// ============================================================================
// Sales Rep API
// ============================================================================

export interface SalesRep {
  id: string
  name: string
  sales_rep_id: string
  email: string | null
}

// ============================================================================
// Reference Data (combined)
// ============================================================================

/**
 * Trimmed user record used for inline assignee dropdowns.
 * The full `User` interface includes audit fields we don't need in
 * the selection UI.
 */
export interface AssignableUser {
  id: string
  name: string
  email: string
  role: UserRole
}

// ============================================================================
// MCC Options API (AD-013 Slice 3-Lite §2.4)
// ============================================================================

/**
 * AGMS industry bucket (one of 6) — drives the Slice 4b.1 fee class
 * registry lookup. Keep in sync with backend
 * config/agms_mcc_options.php + Slice 4b.1 buildPricing().
 */
export type AgmsMccBucket =
  | 'restaurant'
  | 'retail'
  | 'supermarket'
  | 'convenience_store'
  | 'cnp_moto'
  | 'ecommerce'

export interface MccOption {
  /** 4-digit MCC, written to applications.mcc_code at submit. */
  mcc: string
  /** Human-friendly UI label; bucket prefix included for readability. */
  label: string
  /** Industry bucket — determines the Slice 4b.1 fee class block. */
  bucket: AgmsMccBucket
  /** SIC reference (often = mcc, retained for ops). */
  sic: string
}

// ============================================================================
// Document API
// ============================================================================

export type DocumentCategory =
  | 'mpa' | 'id_passport' | 'voided_check' | 'statements'
  | 'pl' | 'supporting_docs' | 'images' | 'var'

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  mpa: 'Merchant Processing Agreement',
  id_passport: 'ID / Passport',
  voided_check: 'Voided Check',
  statements: 'Bank Statements',
  pl: 'Profit & Loss',
  supporting_docs: 'Supporting Documents',
  images: 'Images',
  var: 'VAR Sheet',
}

export const RESTRICTED_CATEGORIES: DocumentCategory[] = ['voided_check', 'var']

export const MERCHANT_UPLOADABLE_CATEGORIES: DocumentCategory[] = [
  'id_passport', 'voided_check', 'statements', 'pl', 'supporting_docs',
]

export const PDF_ONLY_CATEGORIES: DocumentCategory[] = ['mpa', 'voided_check', 'var', 'pl']

/**
 * IRIS-style flat document row. The backend April 20, 2026 redesign
 * moved from 8 fixed categories to free-form `label` + `is_restricted`.
 * Synthetic rows (BoldSign signed MPA) use id='signed-mpa'.
 */
export interface LeadDocument {
  id: string
  label: string
  category: DocumentCategory | null
  is_restricted: boolean
  file_name: string
  mime_type: string | null
  file_size: number | null
  version: number
  created_at: string | null
  uploaded_by: string
  uploaded_by_merchant: boolean
  download_url: string
  is_synthetic: boolean
}

export interface DocumentListResponse {
  documents: LeadDocument[]
  suggested_labels: string[]
  restricted_default_labels: string[]
}

export interface PdfTemplateOption {
  key: string
  label: string
  name: string
  summary: string
}

export interface GeneratedDocumentResponse {
  id: string
  label: string
  file_name: string
  mime_type: string
  version: number
  message: string
}

export interface DocumentRequestPayload {
  categories: DocumentCategory[]
  merchant_email: string
  merchant_name?: string
  message?: string
}

export interface DocumentRequestResponse {
  document_request_id: string
  upload_url: string
  expires_at: string
  message: string
}

export interface MerchantUploadInfo {
  merchant_name: string | null
  categories: DocumentCategory[]
  uploaded_categories: DocumentCategory[]
  remaining_categories: DocumentCategory[]
  expires_at: string
  message: string | null
  category_rules: Record<string, { label: string; allowed_types: string; max_size_mb: number }>
}

export interface DocumentUploadPayload {
  file: File
  label: string
  is_restricted?: boolean
  /** Optional legacy category — primarily used by internal callers still on the old shape. */
  category?: DocumentCategory
}

export interface DocumentUploadResponse {
  id: string
  label: string
  is_restricted: boolean
  category: DocumentCategory | null
  file_name: string
  version: number
  message: string
}

export interface DocumentPreviewResponse {
  id: string
  file_name: string
  mime_type: string | null
  preview_url: string
  expires_in_seconds: number
}

export interface EmailDocumentPayload {
  recipient_email: string
  recipient_name?: string
  subject?: string
  message?: string
}

export interface BulkDeleteResponse {
  deleted_count: number
  message: string
}

export interface BulkDownloadResponse {
  download_url: string
  expires_in_seconds: number
  file_count: number
}

// Public merchant upload API (no auth token — uses raw axios)
export interface ReturnUploadInfo {
  document_type: string
  max_file_size_mb: number
  allowed_types: string
  expires_at: string
}

// ============================================================================
// Notification API
// ============================================================================

export interface CrmNotification {
  id: string
  type: string
  data: Record<string, unknown>
  read_at: string | null
  created_at: string
}

// ============================================================================
// Document Request List API
// ============================================================================

export type DocumentRequestStatus = 'pending' | 'fulfilled' | 'expired' | 'voided'

export interface DocumentRequestSummary {
  id: string
  merchant_email: string
  merchant_name: string | null
  categories: DocumentCategory[]
  uploaded_categories: DocumentCategory[]
  remaining_categories: DocumentCategory[]
  status: DocumentRequestStatus
  expires_at: string
  requested_by: string | null
  created_at: string
  voided_at: string | null
  voided_by: string | null
}

export interface VoidDocumentRequestResponse {
  id: string
  status: 'voided'
  voided_at: string
}

// ============================================================================
// Help Hub API
// ============================================================================

export type ArticleStatus = 'draft' | 'pending_review' | 'published' | 'archived'

export interface ArticleCategory {
  id: string
  name: string
  slug: string
  description: string | null
  icon: string | null
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ArticleTag {
  id: string
  name: string
  slug: string
  created_at: string
  updated_at: string
}

export interface Article {
  id: string
  title: string
  slug: string
  category_id: string
  category: ArticleCategory | null
  body: string
  excerpt: string | null
  status: ArticleStatus
  ai_draft?: boolean
  author_id: string
  author: Pick<User, 'id' | 'name' | 'role'> | null
  reviewer_id: string | null
  reviewer: Pick<User, 'id' | 'name' | 'role'> | null
  published_at: string | null
  rejected_at: string | null
  rejection_note: string | null
  view_count: number
  helpful_count: number
  not_helpful_count: number
  tickets_solved_count: number | null
  versions_count: number
  visibility_roles: string[]
  tags: ArticleTag[]
  created_at: string
  updated_at: string
}

export interface ArticleVersion {
  id: string
  article_id: string
  version_number: number
  title: string
  body: string
  edited_by_id: string | null
  editor: Pick<User, 'id' | 'name' | 'role'> | null
  change_note: string | null
  created_at: string
}

export interface ArticleComment {
  id: string
  article_id: string
  user_id: string
  user: Pick<User, 'id' | 'name' | 'role'> | null
  body: string
  resolved_at: string | null
  resolved_by_id: string | null
  resolver: Pick<User, 'id' | 'name' | 'role'> | null
  created_at: string
}

export interface TicketArticleSuggestion {
  id: string
  title: string
  slug: string
  excerpt: string | null
}

export interface TicketArticleSuggestionOutcome {
  id: string
  opened: boolean
  article_id: string | null
  ticket_id: string | null
  token: string
}

export interface ArticlePayload {
  title: string
  category_id: string
  body: string
  excerpt?: string | null
  visibility_roles?: string[]
  tags?: string[]
  change_note?: string | null
}

export { authApi, twoFactorApi, firstLoginApi, forgotPasswordApi } from './api/auth'
export { userAdminApi } from './api/users'
export { pricingTemplateAdminApi, pricingTemplateApi } from './api/pricingTemplates'
export { leadApi } from './api/leads'
export { applicationApi } from './api/applications'
export { deploymentApi } from './api/deployment'
export { ticketApi, ticketTypeApi } from './api/tickets'
export { merchantMaintenanceApi } from './api/merchantMaintenance'
export { merchantReportingApi } from './api/merchantReporting'
export { salesRepApi, assignableUsersApi, mccOptionApi, referenceApi } from './api/reference'
export { documentApi, merchantUploadApi, returnUploadApi, documentRequestApi } from './api/documents'
export { notesApi } from './api/notes'
export { appointmentsApi } from './api/appointments'
export { googleCalendarApi } from './api/googleCalendar'
export { notificationApi } from './api/notifications'
export { helpApi } from './api/help'

export default api
